<script>
export default {
  name: "DidoMillesimeInputFilterGeometry",
};
</script>

<script setup>
import { ref, watch } from "vue";
import L from "leaflet";
import "leaflet-draw";
import union from "@turf/union";
import { showNotification } from "../../../utils";

import leafletIconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import leafletIconUrl from "leaflet/dist/images/marker-icon.png";
import leafletShadowUrl from "leaflet/dist/images/marker-shadow.png";

// setting for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: leafletIconRetinaUrl,
  iconUrl: leafletIconUrl,
  shadowUrl: leafletShadowUrl,
});
L.drawLocal.draw.toolbar.buttons.polygon = "Dessiner un polygône";
L.drawLocal.draw.toolbar.actions = {
  title: "Annuler le dessin du polygône",
  text: "Annuler le polygône",
};
L.drawLocal.draw.toolbar.finish = {
  title: "Terminer le dessin du polygône",
  text: "Terminer le polygône",
};
L.drawLocal.draw.toolbar.undo = {
  title: "Effacer le dernier point dessiné",
  text: "Effacer le dernier point",
};
L.drawLocal.draw.handlers.polygon.tooltip = {
  start: "Cliquer pour commencer à dessiner un ploygône",
  cont: "Cliquer pour continuer à dessiner le polygône",
  end: "Cliquer sur le premier point pour terminer ce polygône",
};
L.drawLocal.edit.toolbar.buttons.edit = "Éditer le polygône";
L.drawLocal.edit.toolbar.buttons.editDisabled = "Aucun polygône à éditer";
L.drawLocal.edit.toolbar.buttons.remove = "Supprimer des polygones";
L.drawLocal.edit.toolbar.buttons.removeDisabled = "Aucun polygône à supprimer";
L.drawLocal.edit.toolbar.actions.save = {
  title: "Sauvegarder les changements sur le polygône",
  text: "Sauvegarder les changements",
};
L.drawLocal.edit.toolbar.actions.cancel = {
  title: "Annuler les changements réalisés sur le polygône",
  text: "Annuler les changements",
};
L.drawLocal.edit.toolbar.actions.clearAll = {
  title: "Supprimer tous les polygônes",
  text: "Tout supprimer",
};
L.drawLocal.edit.handlers.edit.tooltip = {
  text: "Bouger ou ajouter des points pour éditer le polygône.",
  subtext: "Vous pouvez annuler les changements",
};
L.drawLocal.edit.handlers.remove.tooltip.text =
  "Cliquer sur un polygône pour le supprimer";

// define Props
const props = defineProps({
  modelValue: { type: String, required: false, default: "" },
  label: { type: Boolean, required: true },
});

// define watchers
watch(
  () => [props.modelValue],
  () => (inputValue.value = props.modelValue),
);

// define Emits
const emit = defineEmits(["update:model-value"]);

// define reactive data
const inputValue = ref(props.modelValue);
const dialogOpened = ref(false);
const geometry = ref(null);
const center = ref([46.71109, 1.7191036]);
let map = null;

// define ref for map dom element
const mapEl = ref(null);

// define methods
const mountMap = () => {
  // create Leaflet Map (disable default zoom button and set center of map)
  map = L.map(mapEl.value, { zoomControl: false }).setView(center.value, 6);

  // Set up the OSM layer
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: 'Data © <a href="http://osm.org/copyright">OpenStreetMap</a>',
    maxZoom: 18,
  }).addTo(map);

  // Initialise the FeatureGroup to store editable layers
  const polygonsGroup = new L.FeatureGroup();
  map.addLayer(polygonsGroup);

  // Add control for adding geometry directly from input
  const InputGeoJsonControl = L.Control.extend({
    options: { position: "topleft" },
    container: null,
    inputNode: null,
    wrapbuttonNode: null,
    buttonNode: null,
    onAdd() {
      this.container = L.DomUtil.create("div", "");
      this.inputNode = L.DomUtil.create("input", "", this.container);
      this.inputNode.style = "min-width: 450px;";
      this.inputNode.placeholder =
        "Vous pouvez saisir directement une géométrie geojson et l'ajouter à la carte";
      this.wrapbuttonNode = L.DomUtil.create("span", "", this.container);
      this.buttonNode = L.DomUtil.create("button", "", this.wrapbuttonNode);
      this.buttonNode.type = "button";
      this.buttonNode.title = "Ajouter le geojson";
      this.buttonNode.innerHTML = "+";
      this.buttonNode.style =
        "margin-left: 2px; border: 2px solid rgba(0,0,0,0.2); border-radius: 4px; font-weight: bold; cursor: pointer;";

      L.DomEvent.disableClickPropagation(this.container);
      L.DomEvent.on(this.container, "contextmenu", (ev) =>
        L.DomEvent.stopPropagation(ev),
      );
      L.DomEvent.disableScrollPropagation(this.container);
      L.DomEvent.on(this.buttonNode, "click", () => {
        try {
          const geometry = this.inputNode.value
            ? JSON.parse(this.inputNode.value)
            : "";
          const geojson = { type: "Feature", properties: {}, geometry };
          L.geoJson(geojson).eachLayer((layer) => {
            polygonsGroup.addLayer(layer);
            updateGeoJson(polygonsGroup);
          });
          this.inputNode.value = "";
        } catch (error) {
          showNotification({
            color: "negative",
            icon: "mdi-alert-decagram",
            message: "La géométrie ne semble pas être du GeoJson valide",
            position: "top",
            timeout: 2500,
          });
        }
      });
      return this.container;
    },
    onRemove() {},
  });
  const inputGeoJsonControl = new InputGeoJsonControl();
  map.addControl(inputGeoJsonControl);

  // Add custom zoom control
  L.control.zoom({ position: "topleft" }).addTo(map);

  // Set options of Draw Plugin
  const drawPluginOptions = {
    position: "topright",
    draw: {
      polygon: {
        allowIntersection: false, // Restricts shapes to simple polygons
        drawError: {
          color: "#e1e100", // Color the shape will turn when intersects
          message:
            "<strong>Sélection invalide<strong> Vous ne pouvez pas dessiner ce type de forme", // Message that will show when intersect
        },
        shapeOptions: {
          color: "#97009c",
        },
      },
      // disable toolbar item by setting it to false
      polyline: false,
      circle: false,
      circlemarker: false,
      rectangle: false,
      marker: false,
    },
    edit: {
      featureGroup: polygonsGroup,
    },
  };

  // Initialise the draw control and pass it the FeatureGroup of editable layers
  const drawControl = new L.Control.Draw(drawPluginOptions);
  map.addControl(drawControl);

  // Set events when drawing polygons on map
  map.on("draw:created", (e) => {
    polygonsGroup.addLayer(e.layer);
    updateGeoJson(polygonsGroup);
  });
  map.on("draw:edited", () => {
    updateGeoJson(polygonsGroup);
  });
  map.on("draw:deleted", () => {
    updateGeoJson(polygonsGroup);
  });

  // Initilize map with a polygon from actual value
  geometry.value = inputValue.value ? JSON.parse(inputValue.value) : "";
  if (geometry.value) {
    const geojson = {
      type: "Feature",
      properties: {},
      geometry: geometry.value,
    };
    L.geoJson(geojson).eachLayer((layer) => {
      polygonsGroup.addLayer(layer);
      updateGeoJson(polygonsGroup);
    });
  }
};
const updateGeoJson = (polygonsGroup) => {
  let geojson = null;
  let i = 0;
  polygonsGroup.eachLayer((layer) => {
    if (i === 0) geojson = layer.toGeoJSON();
    if (i > 0) geojson = union(geojson, layer.toGeoJSON());
    i += 1;
  });
  geometry.value = geojson ? geojson.geometry : "";
};
const closeDialog = () => {
  inputValue.value = geometry.value ? JSON.stringify(geometry.value) : "";
  emit(
    "update:model-value",
    geometry.value ? JSON.stringify(geometry.value) : "",
  );
  dialogOpened.value = false;
};
</script>

<template>
  <QInput
    v-model="inputValue"
    dense
    square
    outlined
    :label="label ? 'Géométrie au format GeoJson' : ''"
    stack-label
    class="dido-millesimefilter-geometry"
    clearable
    clear-icon="mdi-close"
    debounce="500"
    @click="dialogOpened = true"
    @update:model-value="emit('update:model-value', inputValue)"
  >
    <template #append>
      <QBtn round dense flat icon="mdi-target" @click="dialogOpened = true" />
    </template>
  </QInput>
  <QDialog
    v-model="dialogOpened"
    persistent
    full-width
    full-height
    transition-show="scale"
    transition-hide="scale"
    @show="mountMap"
  >
    <QCard
      class="bg-secondary text-white dido-millesimefilter-geometry-dialogcard"
    >
      <QCardSection>
        <QBar class="bg-secondary text-overline">
          Dessinez la zone de recherche
          <QSpace />
          <QBtn v-close-popup dense flat icon="mdi-window-close" />
        </QBar>
      </QCardSection>

      <QCardSection
        class="dido-millesimefilter-geometry-dialogcard-mapsection q-pt-none"
      >
        <div
          ref="mapEl"
          class="dido-millesimefilter-geometry-dialogcard-leafletmap"
        />
      </QCardSection>

      <QCardActions align="right" class="bg-white text-secondary">
        <QBtn flat label="OK" @click="closeDialog" />
      </QCardActions>
    </QCard>
  </QDialog>
</template>

<style lang="sass" scoped>
.dido-millesimefilter-geometry
  min-width: 150px
.dido-millesimefilter-geometry-dialogcard-mapsection
  height: calc(100vh - 48px - 64px - 52px)
.dido-millesimefilter-geometry-dialogcard-leafletmap
  height: 100%
  width: 100%
</style>

<style lang="sass">
@use 'sass:meta'
.dido-millesimefilter-geometry-dialogcard
  @include meta.load-css("leaflet/dist/leaflet.css")
  @include meta.load-css("leaflet-draw/dist/leaflet.draw.css")
</style>
